import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SFService } from '../service/SF.service';
import { ContactService } from 'service/contact.service';
import axios from 'axios';
import { LeadsService } from 'service/leads.service';
import { HuddleClientService } from 'service/huddle-client.service';
/**
 * Custom hook to handle file selection and generate a preview URL for a single image.
 * * @returns An object containing the necessary state, ref, and handlers.
 */
type UploadProfilePictureOptions = {
  key?: boolean;
  type?: 'sf' | 'contact' | 'leads' | 'client';
};

const upload = {
  contact: {
    presignedUrl: async (selectedFile: File) => {
      return await ContactService.getPresigned(
        selectedFile.size.toString(),
        selectedFile.type,
        selectedFile.name,
      );
    },
  },
  sf: {
    presignedUrl: async (selectedFile: File) => {
      const email = sessionStorage.getItem('current-user') || '';
      return await SFService.getPresigned(email, selectedFile.type, selectedFile.name);
    },
  },
  leads: {
    presignedUrl: async (selectedFile: File) => {
      return await LeadsService.getProfileImageUrl(
        selectedFile.name,
        selectedFile.type,
        selectedFile.size.toString(),
      );
    },
  },
  client: {
    presignedUrl: async (selectedFile: File) => {
      return await HuddleClientService.getPresignedUrl(
        selectedFile.name,
        selectedFile.type,
        selectedFile.size.toString(),
      );
    },
  },
};

export const useImagePreview = (initialUrl?: string | null) => {
  const [selectedFile, setSelectedFile] = useState<File | string | null>(initialUrl || null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const DEFAULT_UPLOAD_OPTIONS = {
    key: false,
    type: 'sf',
  };

  useEffect(() => {
    if (initialUrl !== previewUrl) {
      setSelectedFile(initialUrl || null);
      setPreviewUrl(initialUrl || null);
    }
  }, [initialUrl]);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      const file = fileList?.[0] || null;

      if (file) {
        setSelectedFile(file);
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else if (fileList && fileList.length === 0) {
        setSelectedFile(initialUrl || null);
        setPreviewUrl(initialUrl || null);
      } else {
        setSelectedFile(null);
        setPreviewUrl(null);
      }
    },
    [initialUrl],
  );

  const clearSelection = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const openFileSelection = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadProfilePicture = async (props?: UploadProfilePictureOptions) => {
    const options = { ...DEFAULT_UPLOAD_OPTIONS, ...props };

    if (selectedFile && selectedFile instanceof File) {
      const getPresigned =
        await upload[options?.type as 'contact' | 'sf' | 'leads' | 'client'].presignedUrl(
          selectedFile,
        );

      const uploadFields = getPresigned?.fields ?? getPresigned?.fileds ?? {};
      const contentType = uploadFields['Content-Type'] ?? selectedFile.type;
      const uploadEndpoint = getPresigned?.url ?? getPresigned?.uploadUrl;

      await axios.post(
        uploadEndpoint,
        { ...uploadFields, file: selectedFile },
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      const uploadedKey =
        uploadFields.key ?? getPresigned?.key ?? getPresigned?.s3Key ?? selectedFile.name;
      const uploadBaseUrl = getPresigned?.url ?? getPresigned?.uploadUrl ?? '';

      if (options.key) {
        return uploadedKey;
      }
      return uploadBaseUrl ? `${uploadBaseUrl}${uploadedKey}` : uploadedKey;
    }
  };

  return {
    fileInputRef,
    selectedFile,
    previewUrl,
    handleFileChange,
    clearSelection,
    openFileSelection,
    uploadProfilePicture,
  };
};
